import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { selectAdminToken } from "../sessionSlice";
import { getPublishStatus, publishGame } from "../../functions/crossroadsApi";
import "./AdminNav.css";

/** Fired by admin pages after saving, so the publish status refreshes. */
export const CONTENT_CHANGED_EVENT = "cr-admin-content-changed";
export function notifyContentChanged() {
  window.dispatchEvent(new Event(CONTENT_CHANGED_EVENT));
}

function formatWhen(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/** Admin section tabs + publish status / Publish (PRD 5.6, 5.7). */
export default function AdminNav() {
  const navigate = useNavigate();
  const token = useSelector(selectAdminToken);
  const currentTheme = useSelector((s) => s.theme.currentTheme);
  const [status, setStatus] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const refresh = useCallback(() => {
    if (!token) return;
    getPublishStatus({ token, currentTheme })
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [token, currentTheme]);

  useEffect(() => {
    refresh();
    window.addEventListener(CONTENT_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(CONTENT_CHANGED_EVENT, refresh);
  }, [refresh]);

  const publish = async () => {
    const count = status?.draftQuestionCount ?? 0;
    const r = await Swal.fire({
      title: "Publish the game?",
      html: `Players will see your current settings and <b>${count}</b> crossroads. Games already in progress pick up the change on their next load.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Publish",
      cancelButtonText: "Not yet",
    });
    if (!r.isConfirmed) return;
    setPublishing(true);
    try {
      await publishGame({ token, currentTheme });
      Swal.fire("Published", "Players now see the latest version of your game.", "success");
      refresh();
    } catch (err) {
      Swal.fire("Couldn't publish", err.message, "error");
    } finally {
      setPublishing(false);
    }
  };

  let statusText = "Checking status…";
  let statusTone = "muted";
  if (status) {
    if (!status.published) {
      statusText = "Not published yet — players can't play until you publish";
      statusTone = "warn";
    } else if (status.hasChanges) {
      statusText = `Unpublished changes · last published ${formatWhen(status.publishedAt)}`;
      statusTone = "warn";
    } else {
      statusText = `Published ${formatWhen(status.publishedAt)} · up to date`;
      statusTone = "ok";
    }
  }

  return (
    <nav className="an-bar" aria-label="Game admin" data-tour="admin-nav">
      <div className="an-tabs">
        <button type="button" className="an-back" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" aria-hidden /> Themes
        </button>
        <NavLink to="/admin/rules" className={({ isActive }) => `an-tab${isActive ? " is-active" : ""}`}>
          <i className="fa-solid fa-sliders" aria-hidden /> Game Settings
        </NavLink>
        <NavLink to="/admin/themeupdate" className={({ isActive }) => `an-tab${isActive ? " is-active" : ""}`}>
          <i className="fa-solid fa-code-fork" aria-hidden /> Crossroads
        </NavLink>
        <NavLink to="/admin/live" className={({ isActive }) => `an-tab${isActive ? " is-active" : ""}`} data-tour="admin-live">
          <i className="fa-solid fa-tower-broadcast" aria-hidden /> Live Control
        </NavLink>
        <NavLink to="/admin/reports" className={({ isActive }) => `an-tab${isActive ? " is-active" : ""}`}>
          <i className="fa-solid fa-chart-column" aria-hidden /> Reports
        </NavLink>
      </div>
      <div className="an-actions">
        <span className={`an-status an-status--${statusTone}`} role="status">
          {statusText}
        </span>
        <button
          type="button"
          className="an-btn an-btn--primary"
          onClick={publish}
          disabled={publishing || !token}
          data-tour="admin-publish"
        >
          <i className="fa-solid fa-rocket" aria-hidden /> {publishing ? "Publishing…" : "Publish"}
        </button>
      </div>
    </nav>
  );
}
